import type { ShopManager } from '@benzinga/shop-manager';
import type { SettingsFormState } from '../_forms';

export interface ChargebeeCardHandle {
  clear: () => void;
  tokenize: () => Promise<{ token?: string } | undefined>;
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined;

/** Chargebee/accounts errors arrive as `{ token: ['…'] }` or `['…']`. */
const firstString = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  return undefined;
};

/**
 * The accounts API rejects with the `Response` on `err.data`; legacy read
 * `body[0]` or `body.token[0]` and fell back to the raw result.
 */
const addCardError = async (err: unknown): Promise<string> => {
  const data = asRecord(err)?.data;
  const body = data instanceof Response ? await data.json().catch(() => undefined) : undefined;
  const record = asRecord(body);
  return (
    // `['Card declined.']`, `{ token: ['…'] }`, or `{ message: '…' }`.
    firstString(body) ??
    firstString(record?.token) ??
    firstString(record?.message) ??
    firstString(asRecord(err)?.message) ??
    'Error adding card. Please try again.'
  );
};

export interface AddCardDeps {
  getCard: () => ChargebeeCardHandle | null;
  shopManager: Pick<ShopManager, 'addCreditCard'>;
}

export const addCardAction =
  ({ getCard, shopManager }: AddCardDeps) =>
  async (): Promise<SettingsFormState> => {
    const card = getCard();
    if (!card) {
      return { message: 'The payment form is still loading — try again in a moment.', status: 'error' };
    }

    let token: string | undefined;
    try {
      token = (await card.tokenize())?.token;
    } catch (err) {
      // Legacy precedence: Chargebee's field error, then the thrown message.
      return {
        message:
          firstString(asRecord(err)?.token) ??
          firstString(asRecord(err)?.message) ??
          firstString(err) ??
          'Error adding card. Please try again.',
        status: 'error',
      };
    }
    if (!token) {
      return { message: 'Error adding card. Please try again.', status: 'error' };
    }

    const result = await shopManager.addCreditCard(token);
    if (result.err) {
      return { message: await addCardError(result.err), status: 'error' };
    }

    card.clear();
    return { message: 'Card added.', status: 'success' };
  };

/**
 * Delete a saved card. Callers must keep the legacy guard that the last card on file
 * cannot be removed while a subscription is auto-renewing.
 */
export const removeCard = async (
  shopManager: Pick<ShopManager, 'deleteCreditCard'>,
  cardUuid: string,
): Promise<SettingsFormState> => {
  const result = await shopManager.deleteCreditCard(cardUuid);
  if (result.err) {
    return { message: result.err.message || 'Unable to remove this card. Please try again.', status: 'error' };
  }
  return { message: 'Card removed.', status: 'success' };
};
